import { MessageCircle, Phone, Video, ArrowRight, Sparkles } from "lucide-react";
import liquidGradient from "../assets/images/main-body-bg1.svg";

const Hero = () => {
    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Animated Liquid Gradient Background */}
            <div className="absolute inset-0 z-0">
                <div
                    className="absolute inset-0 scale-150 animate-gradient-shift"
                    style={{
                        backgroundImage: `url(${liquidGradient})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                {/* Overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background/90" />
            </div>

            {/* Floating Orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-[10%] w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px] animate-float-slow" />
                <div className="absolute top-[30%] right-[5%] w-[400px] h-[400px] rounded-full bg-accent-foreground/25 blur-[100px] animate-float-medium" />
                <div className="absolute bottom-[10%] left-[20%] w-[600px] h-[600px] rounded-full bg-[hsl(200,80%,50%)]/15 blur-[140px] animate-float-fast" />
                <div className="absolute bottom-[30%] right-[20%] w-[300px] h-[300px] rounded-full bg-primary/15 blur-[80px] animate-pulse-slow" />
            </div>

            {/* Particle Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-foreground/20 rounded-full animate-particle"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${3 + Math.random() * 4}s`,
                        }}
                    />
                ))}
            </div>

            {/* Content */}
            <div className="container relative z-10 px-6 pt-32 pb-20">
                <div className="max-w-5xl mx-auto text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-strong mb-8 animate-fade-in-up border border-primary/20">
                        <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                        <span className="text-sm font-medium text-foreground/90">The Future of Creator Connection</span>
                    </div>

                    {/* Main Heading */}
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.1] tracking-tight mb-8 animate-fade-in-up animate-delay-100">
                        <span className="text-foreground">Talk to Your</span>
                        <br />
                        <span className="gradient-text-vibrant">Creators' AI Twin</span>
                    </h1>

                    {/* Subheading */}
                    <p className="text-lg md:text-xl lg:text-2xl text-foreground/70 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up animate-delay-200 font-light">
                        Intimate conversations with digital twins of your favorite creators.
                        <span className="text-foreground/90 font-medium"> Text, call, video chat</span> — anytime.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-16 animate-fade-in-up animate-delay-300">
                        <button type="button" className="theme-solid-btn">
                            Start Chatting
                            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button className="theme-outline-btn">
                            Explore Creators
                        </button>
                    </div>

                    {/* Feature Pills */}
                    <div className="flex flex-wrap items-center justify-center gap-4 animate-fade-in-up animate-delay-400">
                        <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-strong hover:scale-105 transition-transform cursor-default">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-primary" />
                            </div>
                            <span className="font-medium text-foreground/90">Text Chat</span>
                        </div>
                        <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-strong hover:scale-105 transition-transform cursor-default">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-foreground/20 to-accent-foreground/5 flex items-center justify-center">
                                <Phone className="w-5 h-5 text-accent-foreground" />
                            </div>
                            <span className="font-medium text-foreground/90">Voice Calls</span>
                        </div>
                        <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-strong hover:scale-105 transition-transform cursor-default">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(200,80%,50%)]/20 to-[hsl(200,80%,50%)]/5 flex items-center justify-center">
                                <Video className="w-5 h-5 text-[hsl(200,80%,50%)]" />
                            </div>
                            <span className="font-medium text-foreground/90">Video Chat</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Gradient Fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />

            {/* Scroll Indicator */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 animate-bounce-slow">
                <div className="w-7 h-12 rounded-full border-2 border-foreground/20 flex items-start justify-center p-2">
                    <div className="w-1.5 h-3 rounded-full bg-foreground/40 animate-scroll-indicator" />
                </div>
            </div>
        </section>
    );
};

export default Hero;
